/**
 * PhaseRail — the audit workspace's left-hand navigator.
 *
 * The four CMS phases as a vertical rail, each carrying a live status line and
 * a done / current / upcoming state derived from the SAME stage→phase map the
 * WorkflowBar uses. The rail is navigation, not a gate: every phase is
 * clickable so you can look ahead or back exactly as the old scroll allowed —
 * the one legal next move still lives in the WorkflowBar. Below the phases, the
 * Binder is a first-class destination (the record room), not a panel buried
 * mid-scroll.
 */
import React from 'react';

// Rail rows for the four phases. Short labels; the stage panel carries the
// full CMS title and explainer.
const PHASES = [
  { key: 'p1', n: 1, label: 'Engagement', leanLabel: 'Engagement' },
  { key: 'p2', n: 2, label: 'Fieldwork', leanLabel: 'Fieldwork' },
  { key: 'p3', n: 3, label: 'Reporting', leanLabel: 'Reporting' },
  { key: 'p4', n: 4, label: 'Close out', leanLabel: 'Close out' },
];

function dot(state, n) {
  if (state === 'done') return <span className="flex-none w-[22px] h-[22px] rounded-full grid place-items-center text-[11px] font-extrabold bg-emerald-50 text-emerald-600 border-[1.5px] border-emerald-200">✓</span>;
  if (state === 'current') return <span className="flex-none w-[22px] h-[22px] rounded-full grid place-items-center text-[11px] font-extrabold text-white bg-gradient-to-br from-orange-500 to-orange-600">{n}</span>;
  return <span className="flex-none w-[22px] h-[22px] rounded-full grid place-items-center text-[11px] font-extrabold bg-white text-slate-400 border-[1.5px] border-slate-300">{n}</span>;
}

export default function PhaseRail({ currentPhase, activeView, onSelect, isFocused, statuses, binderCount }) {
  const row = (p) => {
    const state = p.n < currentPhase ? 'done' : p.n === currentPhase ? 'current' : 'upcoming';
    const active = activeView === p.key;
    return (
      <button key={p.key} type="button" onClick={() => onSelect(p.key)}
        aria-current={active ? 'true' : undefined}
        className={`w-full flex gap-2.5 text-left p-2.5 rounded-[10px] mt-0.5 transition-colors ${active ? 'bg-orange-50 shadow-[inset_3px_0_0_#ea580c]' : 'hover:bg-slate-100'}`}>
        {dot(state, p.n)}
        <span className="min-w-0">
          <span className={`block text-[13px] leading-tight ${active ? 'font-bold text-slate-800' : 'font-semibold text-slate-600'}`}>{isFocused ? p.leanLabel : p.label}</span>
          {statuses?.[p.key] && <span className="block text-[10.5px] text-slate-400 mt-0.5">{statuses[p.key]}</span>}
        </span>
      </button>
    );
  };

  return (
    <nav aria-label="Audit phases" className="bg-[#fbfcfe] border border-slate-200 rounded-2xl p-3 xl:sticky xl:top-4">
      <div className="text-[10px] font-bold uppercase tracking-[0.12em] text-slate-400 px-2.5">Phases</div>
      {PHASES.map(row)}

      <div className="mt-4 border-t border-slate-100 pt-3">
        <div className="text-[10px] font-bold uppercase tracking-[0.12em] text-slate-400 px-2.5">The record room</div>
        <button type="button" onClick={() => onSelect('binder')}
          aria-current={activeView === 'binder' ? 'true' : undefined}
          className={`w-full flex gap-2.5 text-left p-2.5 rounded-[10px] mt-1 transition-colors ${activeView === 'binder' ? 'bg-orange-50 shadow-[inset_3px_0_0_#ea580c]' : 'hover:bg-slate-100'}`}>
          <span className="flex-none w-[22px] h-[22px] rounded-full grid place-items-center text-[11px] bg-indigo-50 text-indigo-700 border-[1.5px] border-indigo-200">🗂</span>
          <span className="min-w-0">
            <span className={`block text-[13px] leading-tight ${activeView === 'binder' ? 'font-bold text-slate-800' : 'font-semibold text-slate-600'}`}>Binder — all files</span>
            <span className="block text-[10.5px] text-slate-400 mt-0.5">{binderCount != null ? `${binderCount} folder${binderCount === 1 ? '' : 's'} · drop anything` : 'The audit’s record room'}</span>
          </span>
        </button>
      </div>
    </nav>
  );
}
